import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { DragHandle, Sortable, useDragSensors } from "../../shared/Sortable";
import { goalCardBg } from "../../shared/goalColors";
import { formatDeadlineDate } from "../../shared/deadlines";
import { AddItemButton } from "../../shared/AddItemButton";

// One goal as the Goals page draws it, and Radar's goal groups too (Craig, 25 Oct 2026: "change the display of goals
// (and their related tasks) to look the way they do on the Goals page"): a bar in the goal's colour and, when open,
// its unfinished tasks (`openTasks`, each drawn by `row(task)`) and "+ Add a task". `words` says how many tasks are
// left. onOpenGoal opens the goal, onToggle folds it, onAddTask starts a task for it.
// With onReorderTasks(ordered) each task gets ⠿ (`row(task, handle)`), and the order dropped is the goal's own (its page's
// Your order; Craig, 5 Nov 2026: "Can we add drag handles to them? And let's link that manual sort order").
export function GoalBlock({ goal, words, openTasks, open, onToggle, onOpenGoal, onAddTask, row, dated = false, drag = null, selected = false, onReorderTasks = null }) {
  const sensors = useDragSensors();
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = openTasks.map(t => t.id);
    onReorderTasks(arrayMove([...openTasks], ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <div style={{ marginBottom: open ? 16 : 6 }}>
      <GoalBar goal={goal} words={words} open={open} onToggle={onToggle} onOpenGoal={onOpenGoal} dated={dated} drag={drag} selected={selected} />
      {open && (
        <div>
          {onReorderTasks && openTasks.length > 1 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={openTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {openTasks.map(t => <Sortable key={t.id} id={t.id}>{(handle) => row(t, <DragHandle label={t.text} narrow {...handle} />)}</Sortable>)}
              </SortableContext>
            </DndContext>
          ) : openTasks.map(t => row(t))}
          <AddItemButton onClick={onAddTask} />
        </div>
      )}
    </div>
  );
}

// The bar: everything left of "N tasks to do ▼" opens the goal, those words and the arrow show or hide its tasks
// (Craig, 5 Nov 2026: "limit the expand/collapse tap… Any tap to the left of that text should open the goal"), and ⠿
// (when `drag` is given) moves it. `dated` adds the goal's deadline (the Calendar view). `selected` rings the goal open beside the list on a
// wide screen.
function GoalBar({ goal, words, open, onToggle, onOpenGoal, dated, drag, selected = false }) {
  return (
    <div style={{ display: "flex", alignItems: "stretch", minHeight: 48, background: goalCardBg(goal.color), borderRadius: C.rsm, padding: drag ? "0 0 0 14px" : "0 14px", marginBottom: open ? 4 : 0, boxShadow: drag?.isDragging ? C.shadowMd : C.shadow,
      outline: selected ? `3px solid ${C.text}` : "none", outlineOffset: 2 }}>
      <button onClick={onOpenGoal} aria-label={`Open ${goal.title}`} aria-current={selected ? "true" : undefined}
        style={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 8px 10px 0", background: "none", border: "none", textAlign: "left", fontSize: 16, fontWeight: 700, lineHeight: 1.3, color: C.onGoal, cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ minWidth: 0 }}>{goal.title}</span>
        {dated && goal.deadline && <span style={{ fontSize: 13, fontWeight: 400, whiteSpace: "nowrap" }}>{formatDeadlineDate(goal.deadline)}</span>}
      </button>
      <button onClick={onToggle} aria-expanded={open} aria-label={`${goal.title}: ${words}`}
        style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "0 0 0 4px", background: "none", border: "none", fontSize: 13, color: C.onGoal, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
        <span>{words}</span>
        <span aria-hidden="true" style={{ fontSize: 11 }}>{open ? "▲" : "▼"}</span>
      </button>
      {drag && <DragHandle label={goal.title} color={C.onGoalDim} {...drag} />}
    </div>
  );
}
